// QualifyOpportunity.js — BL-017 (Opportunity Qualification Engine)
//
// Opportunity TRIAGE intake → result → handoff. Answers "is this worth deeper
// analysis?" using the ADR-035 scoring standard (Pursue / Investigate / Reject)
// BEFORE the user commits to a full Deal Analyzer underwrite.
//
// Tokens only (no hex) — verdict badges (.badge-pursue/.badge-investigate/
// .badge-reject) live in the design system. Available to all authenticated
// users (the Deal Analyzer remains the premium product).

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { qualifyOpportunity, updateQualification } from "../lib/qualifications";
import { createLead } from "../lib/leads";
import {
  qualificationStarted,
  qualificationCompleted,
  qualificationVerdictEvent,
  qualificationToDealAnalyzer,
  qualificationOverride,
} from "../lib/qualificationAnalytics";

const ASSET_TYPES = [
  { value: "rv_lot", label: "RV Lot" },
  { value: "rv_park", label: "RV Park" },
  { value: "campground", label: "Campground" },
  { value: "outdoor_hospitality", label: "Outdoor Hospitality" },
  { value: "other", label: "Other" },
];

// The four approved Phase-1 regulatory questions (ADR-035 §2.3).
const REGULATORY_FIELDS = [
  {
    key: "intended_use_permitted",
    label: "Intended use permitted?",
    options: [["yes", "Yes"], ["unknown", "Unknown"], ["no", "No"]],
  },
  {
    key: "zoning_compatible",
    label: "Zoning compatible?",
    options: [["yes", "Yes"], ["unknown", "Unknown"], ["no", "No"]],
  },
  {
    key: "restrictions",
    label: "Restrictions preventing operation?",
    options: [["none", "No Known Restrictions"], ["unknown", "Unknown"], ["present", "Restrictions Present"]],
  },
  {
    key: "utilities_confirmed",
    label: "Utility / infrastructure availability confirmed?",
    options: [["yes", "Yes"], ["unknown", "Unknown"], ["no", "No"]],
  },
];

const VERDICT_BADGE = {
  Pursue: "badge-pursue",
  Investigate: "badge-investigate",
  Reject: "badge-reject",
};

const numOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function QualifyOpportunity() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    formatted_address: "",
    city: "",
    state: "",
    zip: "",
    lat: "",
    lng: "",
    asset_type: "rv_park",
    asking_price: "",
  });
  const [regulatory, setRegulatory] = useState({
    intended_use_permitted: "unknown",
    zoning_compatible: "unknown",
    restrictions: "unknown",
    utilities_confirmed: "unknown",
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setReg = (k) => (e) => setRegulatory((p) => ({ ...p, [k]: e.target.value }));

  // AddressAutocomplete → seed lat/lng + address parts when a place is chosen.
  const onPlaceSelected = (place = {}) => {
    const c = place.address_components || {};
    setForm((p) => ({
      ...p,
      formatted_address: place.formatted_address || p.formatted_address,
      lat: place.lat ?? p.lat,
      lng: place.lng ?? p.lng,
      city: c.locality || c.sublocality || p.city,
      state: c.administrative_area_level_1_short || p.state,
      zip: c.postal_code || p.zip,
    }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const input = {
      lat: numOrNull(form.lat),
      lng: numOrNull(form.lng),
      address: form.formatted_address || null,
      city: form.city || null,
      state: form.state || null,
      zip: form.zip || null,
      asset_type: form.asset_type,
      asking_price: numOrNull(form.asking_price),
      regulatory: { ...regulatory },
    };

    if (input.lat === null || input.lng === null) {
      setError("Enter an address (or latitude/longitude) to qualify this opportunity.");
      return;
    }

    setLoading(true);
    qualificationStarted({ asset_type: input.asset_type });
    const { data, error: apiError } = await qualifyOpportunity(input);
    setLoading(false);

    if (apiError || !data) {
      setError(apiError?.message || "Qualification failed. Please try again.");
      return;
    }

    setResult(data);
    qualificationCompleted({
      recommendation: data.recommendation,
      score_band: data.scoreBand,
      regulatory_state: data.regulatoryState,
    });
    qualificationVerdictEvent(data.recommendation, {
      score_band: data.scoreBand,
      regulatory_state: data.regulatoryState,
    });
  }

  function buildPrefill() {
    return {
      lat: numOrNull(form.lat),
      lng: numOrNull(form.lng),
      formatted_address: form.formatted_address || null,
      city: form.city || null,
      state: form.state || null,
      zip: form.zip || null,
      asking_price: numOrNull(form.asking_price),
    };
  }

  function handleContinue() {
    if (!result) return;
    qualificationToDealAnalyzer({ recommendation: result.recommendation });

    // Reject → user is overriding. Record it (analytics + persistence).
    if (result.recommendation === "Reject") {
      qualificationOverride({
        original_verdict: "Reject",
        score_band: result.scoreBand,
        regulatory_state: result.regulatoryState,
      });
      if (result.qualification?.id) {
        updateQualification({ id: result.qualification.id, overridden: true });
      }
    }

    navigate("/deal-analyzer", { state: { prefill: buildPrefill() } });
  }

  async function handleSaveAsLead() {
    if (!result) return;
    const { data } = await createLead({
      entry_type: "other",
      address: form.formatted_address || null,
      city: form.city || null,
      state: form.state || null,
      zip: form.zip || null,
      lat: numOrNull(form.lat),
      lng: numOrNull(form.lng),
      asking_price: numOrNull(form.asking_price),
    });
    const leadId = data?.lead?.id || null;
    if (result.qualification?.id) {
      updateQualification({
        id: result.qualification.id,
        saved_as_lead: true,
        lead_id: leadId,
      });
    }
  }

  return (
    <div className="page" style={{ maxWidth: "720px", margin: "0 auto", padding: "var(--space-6)" }}>
      <h1>Qualify an Opportunity</h1>
      <p style={{ color: "var(--color-text-muted)" }}>
        A fast triage — should you invest the time to fully analyze this opportunity?
        This is not underwriting or investment advice.
      </p>

      <form onSubmit={handleSubmit} className="card" style={{ padding: "var(--space-6)", marginTop: "var(--space-4)" }}>
        <AddressAutocomplete onPlaceSelected={onPlaceSelected} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)", marginTop: "var(--space-4)" }}>
          <label>
            Latitude
            <input
              aria-label="Latitude"
              type="number"
              step="any"
              value={form.lat}
              onChange={setField("lat")}
            />
          </label>
          <label>
            Longitude
            <input
              aria-label="Longitude"
              type="number"
              step="any"
              value={form.lng}
              onChange={setField("lng")}
            />
          </label>
          <label>
            Asset type
            <select aria-label="Asset type" value={form.asset_type} onChange={setField("asset_type")}>
              {ASSET_TYPES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </label>
          <label>
            Asking price
            <input
              aria-label="Asking price"
              type="number"
              step="any"
              value={form.asking_price}
              onChange={setField("asking_price")}
            />
          </label>
        </div>

        <fieldset style={{ marginTop: "var(--space-5)", border: "none", padding: 0 }}>
          <legend style={{ fontWeight: 600 }}>Regulatory pre-check</legend>
          <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
            Self-declared. A regulatory blocker overrides all financial signals (ADR-023).
          </p>
          {REGULATORY_FIELDS.map((f) => (
            <label key={f.key} style={{ display: "block", marginTop: "var(--space-3)" }}>
              {f.label}
              <select aria-label={f.label} value={regulatory[f.key]} onChange={setReg(f.key)}>
                {f.options.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </label>
          ))}
        </fieldset>

        {error && (
          <p role="alert" style={{ color: "var(--color-danger)", marginTop: "var(--space-4)" }}>{error}</p>
        )}

        <button type="submit" className="btn-primary" disabled={loading} style={{ marginTop: "var(--space-5)" }}>
          {loading ? "Qualifying…" : "Qualify Opportunity"}
        </button>
      </form>

      {result && (
        <div className="card" style={{ padding: "var(--space-6)", marginTop: "var(--space-5)" }} data-testid="qualification-result">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
            <div data-testid="qualification-score" style={{ fontSize: "var(--font-size-3xl, 2rem)", fontWeight: 700 }}>
              {result.qualificationScore}
            </div>
            <span className={`badge ${VERDICT_BADGE[result.recommendation] || ""}`}>
              {result.recommendation}
            </span>
          </div>

          {Array.isArray(result.reasons) && result.reasons.length > 0 && (
            <ul style={{ marginTop: "var(--space-4)" }}>
              {result.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}

          <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-5)" }}>
            <button type="button" className="btn-primary" onClick={handleContinue}>
              Continue to Deal Analyzer
            </button>
            <button type="button" className="btn-secondary" onClick={handleSaveAsLead}>
              Save as Lead
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
